<script setup>
import { ref, onMounted, onUnmounted, computed, watch, markRaw } from 'vue'
import { useStore } from 'vuex'
import { HOST, API } from '@/utils/http'
import { debounce } from 'lodash'

import MapSource from '@/components/map/MapSource.vue'
import MapLayer from '@/components/map/MapLayer.vue'
import LayerRadarTrack from './LayerRadarTrack.vue'
import IconRadar from '@/assets/img/radar.svg'
// Import icons statically for initial loading
import IconArrow from '@/assets/img/arrow.svg'
import IconArrowPrimary from '@/assets/img/arrow-primary.svg'

import { useLayerManager } from '@/composables/useLayerManager'
import { createLayerId, createSourceId } from '@/components/layers/types'
import { useLayerFetch } from '@/components/layers/useLayerFetch'

const emit = defineEmits(['isLoaded'])
const props = defineProps({
  layer: {
    type: Object,
    required: true,
  },
  layerId: {
    type: String,
    required: true,
  },
  beforeId: {
    type: String,
    required: true,
  },
  groupId: {
    type: String,
    required: true,
  },
  options: {
    type: Object,
    required: false,
  },
  code: {
    type: String,
    required: true,
  },
  map: {
    type: Object,
    required: true,
  },
  isVisible: {
    type: Boolean,
    required: true,
  },
  orgId: {
    type: Number,
    required: true,
  },
})

const store = useStore()
const map = computed(() => props.map)
const layerManager = useLayerManager(map)
const selectedRange = computed(() => store.getters['layers/getSelectedRange'])

// Unified layer IDs
const radarLocationSourceId = computed(() => createSourceId(props.groupId, 'radar-location-source'))
const radarLocationCircleLayerId = computed(() => createLayerId(props.groupId, 'radar-location-circle'))
const radarLocationSymbolLayerId = computed(() => createLayerId(props.groupId, 'radar-location-symbol'))
const sweepSourceId = computed(() => createSourceId(props.groupId, 'sweep-source'))
const sweepLayerId = computed(() => createLayerId(props.groupId, 'sweep'))
const trackLinesSourceId = computed(() => createSourceId(props.groupId, 'track-lines-source'))
const trackLinesLayerId = computed(() => createLayerId(props.groupId, 'track-lines'))
const trackPointsSourceId = computed(() => createSourceId(props.groupId, 'track-points-source'))
const trackPointsLayerId = computed(() => createLayerId(props.groupId, 'track-points'))

const debouncedFetchResults = debounce(async () => {
  radar.value = await fetchResults()
}, 300)

watch(selectedRange, (newRange, oldRange) => {
  if (newRange !== oldRange && props.isVisible) {
    debouncedFetchResults()
  }
}, { deep: true })

const fetchResults = async () => {
  store.commit('SET_IS_LOADING_DATA', true)
  try {
    const selectedRange = store.getters['layers/getSelectedRange'] || [];
    let url = `${HOST}/org/${props.orgId}/layers/${props.code}/radar/result`;

    if (selectedRange.length === 2) {
      // Ensure we have valid date strings in UTC format for the server
      const startDate = selectedRange[0];
      const endDate = selectedRange[1];

      url += `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
    }

    const response = await API({
      method: 'GET',
      url
    })

    return response.data
  } catch (e) {
    console.error('Error fetching radar data:', e)
    errorMessage.value = `Failed to load radar data: ${e.message || 'Unknown error'}`
    return null
  } finally {
    store.commit('SET_IS_LOADING_DATA', false)
  }
}

const fetchDates = async () => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${props.orgId}/layers/${props.code}/radar/dates`
    })

    if (response.data && response.data.layers && Array.isArray(response.data.layers)) {
      const transformedLayers = response.data.layers.map(layer => {
        const processedDates = Array.isArray(layer.dates) ? layer.dates.map(dateItem => {
          if (dateItem.date && !dateItem.date.endsWith('Z') && !dateItem.date.includes('+')) {
            dateItem.date = dateItem.date + 'Z';
          }
          return dateItem;
        }) : [];

        return {
          id: layer.id || 'unknown',
          color: layer.color || 'blue',
          dates: processedDates
        };
      });

      return transformedLayers;
    } else if (response.data && response.data.dates) {
      const dates = Array.isArray(response.data.dates) ? response.data.dates : [];

      const processedDates = dates.map(dateItem => {
        if (dateItem.date && !dateItem.date.endsWith('Z') && !dateItem.date.includes('+')) {
          dateItem.date = dateItem.date + 'Z';
        }
        return dateItem;
      });

      return [{
        id: "radar",
        color: "blue",
        dates: processedDates
      }];
    }

    return [{
      id: "radar",
      dates: [],
      color: "blue"
    }];
  } catch (e) {
    console.error('Error fetching radar dates:', e)
    errorMessage.value = `Failed to load radar dates: ${e.message || 'Unknown error'}`
    return [{
      id: "radar",
      dates: [],
      color: "blue"
    }];
  }
}


const errorMessage = ref('')

const radar = ref(null)
const dates = ref(null)
const pollingRate = ref(60000)
const activePopupTrackId = ref(null)

const pollResults = async () => {
  radar.value = await fetchResults()
}

useLayerFetch(computed(() => props.isVisible), {
  onShow: pollResults,
  onHide: () => {
    if (dates.value && Array.isArray(dates.value)) {
      store.commit('layers/REMOVE_DATES', dates.value.map(d => d.id))
    }
  },
  pollInterval: pollingRate.value
})

let needsDates = true

watch(() => props.isVisible, async (visible) => {
  if (visible) {
    if (needsDates) {
      needsDates = false
      dates.value = await fetchDates()
      if (dates.value) {
        if (Array.isArray(dates.value)) {
          store.commit('layers/ADD_DATES', dates.value)
        } else if (dates.value.id) {
          store.commit('layers/ADD_DATES', [dates.value])
        }
      }
    } else if (dates.value && Array.isArray(dates.value)) {
      store.commit('layers/ADD_DATES', dates.value)
    }
  }
})

const addMapImage = (imageId, imageSource) => {
  if (!props.map.hasImage(imageId)) {
    const image = new Image()
    image.onload = () => {
      if (!props.map.hasImage(imageId)) {
        try {
          // Set explicit width and height to ensure proper rendering in Firefox
          image.width = 140
          image.height = 140
          props.map.addImage(imageId, image)
        } catch (error) {
          console.error(`Error adding ${imageId}:`, error)
        }
      }
    }
    image.onerror = (e) => {
      console.error(`Failed to load ${imageId}:`, e)
    }
    image.src = imageSource
  }
}

const radarGeometry = computed(() => {
  if (radar.value) {
    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [radar.value.longitude, radar.value.latitude]
        },
        properties: {}
      }]
    }
  }
  return null
})

const radarSweepGeometry = computed(() => {
  if (!radar.value || !radar.value.sweep) return null

  const sweep = radar.value.sweep
  const center = [radar.value.longitude, radar.value.latitude]

  const radius = 0.1
  const points = 64
  const coordinates = []

  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 360
    const radian = angle * Math.PI / 180
    coordinates.push([
      center[0] + Math.cos(radian) * radius,
      center[1] + Math.sin(radian) * radius
    ])
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates]
    },
    properties: {}
  }
})

const handleRadarClick = () => {
  if (radar.value) {
    props.map.flyTo({
      center: [radar.value.longitude, radar.value.latitude],
      duration: 0
    })
  }
}

// markRaw keeps MapSource's deep watcher from traversing thousands of
// coordinates; the reference change on refetch still triggers setData.
const trackPointsCollection = computed(() => {
  const features = (radar.value?.tracks || [])
    .filter(t => t.track_detection)
    .map(t => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [t.track_detection.lon, t.track_detection.lat]
      },
      properties: {
        id: t.id,
        radar_track_id: t.radar_track_id,
        heading: t.track_detection.heading || 0
      }
    }))

  return markRaw({ type: 'FeatureCollection', features })
})

const trackLinesCollection = computed(() => {
  const features = []

  for (const t of radar.value?.tracks || []) {
    const trackLine = t.track_line
    const geometry = trackLine?.geometry || (trackLine?.type === 'LineString' ? trackLine : null)
    if (!geometry?.coordinates?.length) continue

    features.push({
      type: 'Feature',
      geometry,
      properties: {
        id: t.id,
        radar_track_id: t.radar_track_id
      }
    })
  }

  return markRaw({ type: 'FeatureCollection', features })
})

const isActiveTrack = computed(() => ['==', ['get', 'id'], activePopupTrackId.value ?? -1])

const trackLinesOptions = computed(() => ({
  paint: {
    'line-color': ['case', isActiveTrack.value, '#0059FF', '#62748e'],
    'line-width': ['case', isActiveTrack.value, 4, 3],
    'line-opacity': ['case', isActiveTrack.value, 0.9, 0.7],
    'line-dasharray': [2, 1]
  },
  layout: {
    visibility: props.isVisible ? 'visible' : 'none'
  }
}))

const trackPointsOptions = computed(() => ({
  layout: {
    'icon-image': ['case', isActiveTrack.value, 'arrow-primary-icon', 'arrow-icon'],
    'icon-size': ['case', isActiveTrack.value, 0.18, 0.15],
    'icon-rotate': ['get', 'heading'],
    'icon-rotation-alignment': 'map',
    'icon-allow-overlap': true,
    'symbol-sort-key': ['case', isActiveTrack.value, 1, 0],
    visibility: props.isVisible ? 'visible' : 'none'
  }
}))

const activeTrack = computed(() =>
  radar.value?.tracks?.find(t => t.id === activePopupTrackId.value) || null
)

const handleTrackClick = (e) => {
  const id = e?.features?.[0]?.properties?.id
  activePopupTrackId.value = (!id || id === activePopupTrackId.value) ? null : id
}

const handlePopupClosed = (trackId) => {
  if (activePopupTrackId.value === trackId) {
    activePopupTrackId.value = null
  }
}

onMounted(async () => {
  // Register layers with layer manager
  layerManager.registerLayer(props.groupId, radarLocationCircleLayerId.value)
  layerManager.registerLayer(props.groupId, radarLocationSymbolLayerId.value)
  layerManager.registerLayer(props.groupId, sweepLayerId.value)
  layerManager.registerLayer(props.groupId, trackLinesLayerId.value)
  layerManager.registerLayer(props.groupId, trackPointsLayerId.value)

  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setHours(yesterday.getHours() - 24)

  const range = [
    yesterday.toISOString(),
    now.toISOString()
  ]

  if (!store.getters['layers/getSelectedRange'] || store.getters['layers/getSelectedRange'].length === 0) {
    store.commit('layers/SET_SELECTED_RANGE', range)
  }

  if (props.map) {
    addMapImage('radar-icon', IconRadar)
    addMapImage('arrow-icon', IconArrow)
    addMapImage('arrow-primary-icon', IconArrowPrimary)
  }

  if (props.isVisible) {
    needsDates = false
    dates.value = await fetchDates()

    if (dates.value) {
      try {
        if (Array.isArray(dates.value)) {
          store.commit('layers/ADD_DATES', dates.value)
        } else if (dates.value.id) {
          store.commit('layers/ADD_DATES', [dates.value])
        }
      } catch (error) {
        console.error('Error adding dates to store:', error)
      }
    }
  }

  emit('isLoaded', true)
})

// Watch for organization changes to refetch dates
watch(() => props.orgId, async (newOrgId, oldOrgId) => {
  if (newOrgId && newOrgId !== oldOrgId) {
    // Clear existing dates when switching organizations
    store.commit('layers/CLEAR_DATES');
    // Fetch new dates for the new organization
    dates.value = await fetchDates()

    if (dates.value) {
      try {
        if (Array.isArray(dates.value)) {
          store.commit('layers/ADD_DATES', dates.value)
        } else if (dates.value.id) {
          store.commit('layers/ADD_DATES', [dates.value])
        }
      } catch (error) {
        console.error('Error adding dates to store:', error)
      }
    }
  }
});

onUnmounted(() => {
  layerManager.unregisterLayer(props.groupId, trackPointsLayerId.value)
  layerManager.unregisterLayer(props.groupId, trackLinesLayerId.value)
  layerManager.unregisterLayer(props.groupId, sweepLayerId.value)
  layerManager.unregisterLayer(props.groupId, radarLocationSymbolLayerId.value)
  layerManager.unregisterLayer(props.groupId, radarLocationCircleLayerId.value)

  if (dates.value && Array.isArray(dates.value)) {
    store.commit('layers/REMOVE_DATES', dates.value.map(d => d.id))
  }
})

</script>

<template>
  <div v-if="radar">
    <MapSource
      v-if="radarGeometry"
      :map="map"
      type="geojson"
      :id="radarLocationSourceId"
      :data="radarGeometry"
    >
      <MapLayer
        :map="map"
        :id="radarLocationCircleLayerId"
        type="circle"
        :source="radarLocationSourceId"
        :before-id="beforeId"
        :group-id="groupId"
        :options="{
          paint: {
            'circle-radius': 18,
            'circle-color': '#0059FF',
            'circle-opacity': 0.8,
          },
          layout: {
            visibility: isVisible ? 'visible' : 'none'
          }
        }"
        @click="handleRadarClick"
      />
      <MapLayer
        :map="map"
        :id="radarLocationSymbolLayerId"
        type="symbol"
        :source="radarLocationSourceId"
        :before-id="radarLocationCircleLayerId"
        :group-id="groupId"
        :options="{
          layout: {
            'icon-image': 'radar-icon',
            'icon-size': 0.15,
            'icon-allow-overlap': true,
            visibility: isVisible ? 'visible' : 'none'
          }
        }"
        @click="handleRadarClick"
      />
    </MapSource>

    <MapSource
      v-if="radarSweepGeometry"
      :map="map"
      type="geojson"
      :id="sweepSourceId"
      :data="radarSweepGeometry"
    >
      <MapLayer
        :map="map"
        :id="sweepLayerId"
        type="fill"
        :source="sweepSourceId"
        :before-id="radarLocationSymbolLayerId"
        :group-id="groupId"
        :options="{
          paint: {
            'fill-color': '#0039A3',
            'fill-opacity': 0.1,
            'fill-outline-color': '#0039A3',
          },
          layout: {
            visibility: isVisible ? 'visible' : 'none'
          }
        }"
      />
    </MapSource>

    <MapSource
      :map="map"
      type="geojson"
      :id="trackLinesSourceId"
      :data="trackLinesCollection"
    >
      <MapLayer
        :map="map"
        :id="trackLinesLayerId"
        type="line"
        :source="trackLinesSourceId"
        :before-id="beforeId"
        :group-id="groupId"
        :options="trackLinesOptions"
        @click="handleTrackClick"
      />
    </MapSource>

    <MapSource
      :map="map"
      type="geojson"
      :id="trackPointsSourceId"
      :data="trackPointsCollection"
    >
      <MapLayer
        :map="map"
        :id="trackPointsLayerId"
        type="symbol"
        :source="trackPointsSourceId"
        :before-id="beforeId"
        :group-id="groupId"
        :options="trackPointsOptions"
        @click="handleTrackClick"
      />
    </MapSource>

    <LayerRadarTrack
      v-if="isVisible && activeTrack"
      :key="activeTrack.id"
      :track="activeTrack"
      :code="code"
      :map="map"
      :orgId="orgId"
      @popup-closed="handlePopupClosed"
    />
  </div>
  <!-- Loading/error states hidden from sidebar, shown on detail page -->
</template>
